
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useBateaux } from '@/hooks/useBateaux';
import { Sparkles, Ship, Anchor, Snowflake, List } from 'lucide-react';

const formSchema = z.object({
  bateau_id: z.string().min(1, 'Veuillez sélectionner un navire'),
  nom: z.string().min(1, 'Veuillez entrer un nom pour la checklist'),
  type: z.enum(['Nettoyage', 'Départ', 'Arrivée', 'Hivernage', 'Personnalisé']),
  taches: z.string().min(1, 'Veuillez entrer au moins une tâche'),
});

interface FormulaireCreationChecklistProps {
  onCreerChecklist: (nom: string, type: string, bateauId: string, taches: string[]) => Promise<any>;
}

const FormulaireCreationChecklist = ({ onCreerChecklist }: FormulaireCreationChecklistProps) => {
  const { bateaux } = useBateaux();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      bateau_id: '',
      nom: '',
      type: 'Personnalisé' as const,
      taches: '',
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    setIsSubmitting(true);
    try {
      const tachesList = values.taches
        .split('\n')
        .map(t => t.trim())
        .filter(t => t.length > 0);

      const result = await onCreerChecklist(values.nom, values.type, values.bateau_id, tachesList);
      
      if (!result.error) {
        form.reset();
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Nettoyage':
        return <Sparkles className="h-4 w-4" />;
      case 'Départ':
        return <Ship className="h-4 w-4" />;
      case 'Arrivée':
        return <Anchor className="h-4 w-4" />;
      case 'Hivernage':
        return <Snowflake className="h-4 w-4" />;
      default:
        return <List className="h-4 w-4" />;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Créer une nouvelle checklist</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="bateau_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Navire</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un navire" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {bateaux.map((bateau) => (
                          <SelectItem key={bateau.id} value={bateau.id}>
                            {bateau.nom} - {bateau.nom_constructeur} {bateau.nom_modele}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type de checklist</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Choisir un type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="Nettoyage">
                          <div className="flex items-center gap-2">
                            <Sparkles className="h-4 w-4" />
                            Nettoyage
                          </div>
                        </SelectItem>
                        <SelectItem value="Départ">
                          <div className="flex items-center gap-2">
                            <Ship className="h-4 w-4" />
                            Départ
                          </div>
                        </SelectItem>
                        <SelectItem value="Arrivée">
                          <div className="flex items-center gap-2">
                            <Anchor className="h-4 w-4" />
                            Arrivée
                          </div>
                        </SelectItem>
                        <SelectItem value="Hivernage">
                          <div className="flex items-center gap-2">
                            <Snowflake className="h-4 w-4" />
                            Hivernage
                          </div>
                        </SelectItem>
                        <SelectItem value="Personnalisé">
                          <div className="flex items-center gap-2">
                            <List className="h-4 w-4" />
                            Personnalisé
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="nom"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nom de la checklist</FormLabel>
                  <FormControl>
                    <Input placeholder="Entrer le nom de la checklist" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="taches"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tâches (une par ligne)</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Entrer les tâches, une par ligne&#10;Exemple:&#10;Vérifier le carburant&#10;Contrôler les gilets de sauvetage&#10;Tester la radio VHF"
                      className="min-h-[120px]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button 
              type="submit" 
              className="w-full bg-nautic-gold hover:bg-nautic-lightgold text-nautic"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Création en cours...' : 'Créer la checklist'}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default FormulaireCreationChecklist;
